// src/components/PackingLists/ImageUtils.js

/**
 * Get the medium-sized version of an image URL
 * 
 * @param {string} url - Original Firebase Storage URL
 * @returns {string} URL with _resized_medium added before the file extension
 */
export const getMediumImageUrl = (url) => {
  if (!url) return url;
  
  // Check if URL is a Firebase Storage URL
  if (!url.includes('firebasestorage.googleapis.com')) return url;
  
  // Extract the filename from the URL
  const filenameMatch = url.match(/o\/([^?]+)/);
  if (!filenameMatch || filenameMatch.length < 2) return url;
  
  const encodedFilename = filenameMatch[1]; // This is URL encoded
  const decodedFilename = decodeURIComponent(encodedFilename);
  
  // Remove any existing resize suffix
  const baseFilename = decodedFilename.replace(/_resized_(medium|thumb)/g, '');
  
  // Extract file extension and directory
  const lastDotIndex = baseFilename.lastIndexOf('.');
  if (lastDotIndex === -1) return url; // No extension found
  
  const lastSlashIndex = baseFilename.lastIndexOf('/');
  const directory = lastSlashIndex !== -1 ? baseFilename.substring(0, lastSlashIndex + 1) : '';
  const filename = lastSlashIndex !== -1 ? baseFilename.substring(lastSlashIndex + 1) : baseFilename;
  
  const filenameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
  const extension = filename.substring(filename.lastIndexOf('.'));
  
  // Create new filename with _resized_medium suffix
  const newFilename = `${directory}${filenameWithoutExt}_resized_medium${extension}`;
  
  // Replace the encoded filename in the URL with the new one
  return url.replace(filenameMatch[1], encodeURIComponent(newFilename));
};

/**
 * Get the thumbnail version of an image URL
 * 
 * @param {string} url - Original Firebase Storage URL
 * @returns {string} URL with _resized_thumb added before the file extension
 */
export const getThumbImageUrl = (url) => {
  if (!url) return url;
  
  // Check if URL is a Firebase Storage URL
  if (!url.includes('firebasestorage.googleapis.com')) return url;
  
  // Extract the filename from the URL
  const filenameMatch = url.match(/o\/([^?]+)/);
  if (!filenameMatch || filenameMatch.length < 2) return url;
  
  const encodedFilename = filenameMatch[1]; // This is URL encoded
  const decodedFilename = decodeURIComponent(encodedFilename);
  
  // Remove any existing resize suffix
  const baseFilename = decodedFilename.replace(/_resized_(medium|thumb)/g, '');
  
  // Extract file extension and directory
  const lastDotIndex = baseFilename.lastIndexOf('.');
  if (lastDotIndex === -1) return url; // No extension found
  
  const lastSlashIndex = baseFilename.lastIndexOf('/');
  const directory = lastSlashIndex !== -1 ? baseFilename.substring(0, lastSlashIndex + 1) : '';
  const filename = lastSlashIndex !== -1 ? baseFilename.substring(lastSlashIndex + 1) : baseFilename;
  
  const filenameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
  const extension = filename.substring(filename.lastIndexOf('.'));
  
  // Create new filename with _resized_thumb suffix
  const newFilename = `${directory}${filenameWithoutExt}_resized_thumb${extension}`;
  
  // Replace the encoded filename in the URL with the new one
  return url.replace(filenameMatch[1], encodeURIComponent(newFilename));
};

/**
 * Extract base filename from a Firebase Storage URL
 * 
 * @param {string} url - Firebase Storage URL
 * @returns {string|null} Base filename without resize suffix
 */
export const getBaseFilenameFromUrl = (url) => {
  if (!url) return null;
  
  const filenameMatch = url.match(/o\/([^?]+)/);
  if (!filenameMatch || filenameMatch.length < 2) return null;
  
  const encodedFilename = filenameMatch[1];
  const decodedFilename = decodeURIComponent(encodedFilename);
  
  // Remove any resize suffix from the filename
  return decodedFilename.replace(/_resized_(medium|thumb)/g, '');
};